import { useState, useEffect, useMemo, useRef } from 'react';
import { sAddr } from '../lib/utils';
import { useIsMobile } from '../hooks/useIsMobile';
import { useSubscription } from '../context/SubscriptionContext';
import { usePipeline } from '../hooks/usePipeline';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../supabaseClient';
import { getTrackedStatusChanges, mergeTrackedStatusChangeSnapshot } from '../lib/trackedStatusChanges';
import DACard from './DACard';
import PipelineSummaryView from './PipelineSummaryView';

const COLORS = {
  bg: "#081525", card: "#0a1b36", cardBorder: "#30404e",
  amber: "#d4a84a", amberDark: "#b08830",
  text: "#f0f2f6", textMuted: "#a8b8cc",
  textDim: "#6a80a0", green: "#5aba6a", orange: "#d49060",
};

const STATUS_COLORS = {
  Lodged:            { bg: "#1a2840", text: "#a8b8cc", dot: "#a8b8cc" },
  "Under Assessment":{ bg: "#142e50", text: "#76c9f1", dot: "#76c9f1" },
  "On Exhibition":   { bg: "#2a2418", text: "#d49060", dot: "#d49060" },
  Approved:          { bg: "#142e20", text: "#5aba6a", dot: "#5aba6a" },
  Determined:        { bg: "#142e20", text: "#5aba6a", dot: "#5aba6a" },
  Refused:           { bg: "#2e1a20", text: "#d06868", dot: "#d06868" },
};

function MiniStatusBadge({ status }) {
  const s = STATUS_COLORS[status] || STATUS_COLORS.Lodged;
  return (
    <span style={{
      background: s.bg, color: s.text,
      fontSize: 11, fontWeight: 600, padding: "3px 8px",
      borderRadius: 4, border: `1px solid ${s.text}22`, whiteSpace: "nowrap",
    }}>{status}</span>
  );
}

function CollapsibleSection({ title, defaultOpen = true, children, rightAction }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ marginBottom: open ? 12 : 4 }}>
      <div
        onClick={() => setOpen(!open)}
        style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", cursor: "pointer", userSelect: "none" }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={COLORS.textDim} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
            style={{ transform: open ? "rotate(90deg)" : "rotate(0deg)", transition: "transform 0.2s" }}>
            <polyline points="9 18 15 12 9 6"/>
          </svg>
          <span style={{ color: COLORS.textMuted, fontSize: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>{title}</span>
        </div>
        {open && rightAction}
      </div>
      {open && children}
    </div>
  );
}

function timeAgoStr(isoStr) {
  if (!isoStr) return null;
  const ms = Date.now() - new Date(isoStr).getTime();
  if (ms < 86400000)  return 'Today';
  if (ms < 172800000) return 'Yesterday';
  const d = Math.floor(ms / 86400000);
  return `${d} days ago`;
}

function startOfToday() {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today;
}

function daysUntil(dateStr) {
  if (!dateStr) return null;
  const target = new Date(`${dateStr}T00:00:00`);
  return Math.round((target - startOfToday()) / 86400000);
}

function followUpLabel(dateStr) {
  const days = daysUntil(dateStr);
  if (days === null) return null;
  if (days < 0) return `${Math.abs(days)}d overdue`;
  if (days === 0) return 'Due today';
  if (days === 1) return 'Due tomorrow';
  return `Due in ${days}d`;
}

export default function MyDAs({ apps, tracked, setTracked, togTrack, theme, onShowOnMap, onOpenDA, arrivalStatusChanges = [] }) {
  const [myIn, setMyIn] = useState({ ref: "" });
  const [search, setSearch] = useState("");
  const [expId, setExpId] = useState(null);
  const [pipelineTab, setPipelineTab] = useState('activity');
  const [seenStatuses, setSeenStatuses] = useState({});
  const [showAddDA, setShowAddDA] = useState(false);
  const [addStatus, setAddStatus] = useState("");
  const [addLoading, setAddLoading] = useState(false);
  const seenWriteRef = useRef(new Set());
  const isMobile = useIsMobile(900);
  const { isPro } = useSubscription();
  const { user } = useAuth();
  const { updateStage } = usePipeline(setTracked);

  // Snapshot the persisted "last seen" state for this page visit.
  useEffect(() => {
    if (tracked.length === 0) return;
    setSeenStatuses(prev => {
      const next = { ...prev };
      tracked.forEach(da => {
        if (next[da.id] === undefined) {
          next[da.id] = da.last_seen_status || da.status || null;
        }
      });
      return next;
    });
  }, [tracked]);

  // Mark current statuses as seen in Supabase after the user has had a moment to read them.
  // We keep the local snapshot unchanged so changes remain visible during the current visit.
  useEffect(() => {
    if (!user?.id || tracked.length === 0) return;
    const rows = tracked.filter(da =>
      da.tracked_id &&
      da.status &&
      da.last_seen_status !== da.status &&
      !seenWriteRef.current.has(da.tracked_id)
    );
    if (rows.length === 0) return;

    rows.forEach(da => seenWriteRef.current.add(da.tracked_id));
    let fired = false;
    const timer = setTimeout(() => {
      fired = true;
      rows.forEach(da => {
        supabase
          .from('tracked_das')
          .update({
            last_seen_status: da.status,
            last_seen_status_at: new Date().toISOString(),
          })
          .eq('id', da.tracked_id)
          .eq('user_id', user.id)
          .then(({ error }) => {
            if (error) {
              // Release the reservation so a later effect run can retry.
              seenWriteRef.current.delete(da.tracked_id);
              return;
            }
            setTracked(prev => prev.map(item =>
              item.tracked_id === da.tracked_id
                ? { ...item, last_seen_status: da.status, last_seen_status_at: new Date().toISOString() }
                : item
            ));
          });
      });
    }, 3500);

    return () => {
      clearTimeout(timer);
      // If the timer never fired (tracked changed within the delay), release
      // the reservations — otherwise these rows are never marked seen.
      if (!fired) rows.forEach(da => seenWriteRef.current.delete(da.tracked_id));
    };
  }, [tracked, user?.id]);

  const dueFollowUps = useMemo(() => {
    return tracked
      .filter(da => {
        if (!da.next_follow_up_at) return false;
        if (['won', 'lost', 'not_relevant'].includes(da.pipeline_stage)) return false;
        const days = daysUntil(da.next_follow_up_at);
        return days !== null && days <= 0;
      })
      .sort((a, b) => String(a.next_follow_up_at).localeCompare(String(b.next_follow_up_at)));
  }, [tracked]);

  const upcomingFollowUps = useMemo(() => {
    return tracked
      .filter(da => {
        if (!da.next_follow_up_at) return false;
        if (['won', 'lost', 'not_relevant'].includes(da.pipeline_stage)) return false;
        const days = daysUntil(da.next_follow_up_at);
        return days !== null && days > 0 && days <= 7;
      })
      .sort((a, b) => String(a.next_follow_up_at).localeCompare(String(b.next_follow_up_at)));
  }, [tracked]);

  // Compute changes since last visit. The arrival snapshot is kept for the
  // current visit so the page stays consistent with the bottom-nav badge even
  // after the delayed "mark seen" write refreshes local tracked rows.
  const statusChanges = useMemo(() => {
    return mergeTrackedStatusChangeSnapshot(
      getTrackedStatusChanges(tracked, seenStatuses),
      arrivalStatusChanges,
      tracked
    );
  }, [arrivalStatusChanges, tracked, seenStatuses]);

  // Approaching exhibition deadline (within 7 days)
  const approaching = useMemo(() => {
    const now = Date.now();
    return tracked.filter(da => {
      const end = da.exhibition_end || da.exhibition_end_date;
      if (!end) return false;
      const ms = new Date(end).getTime() - now;
      return ms > 0 && ms < 7 * 86400000;
    });
  }, [tracked]);

  const approvedChangeCount = useMemo(() => (
    statusChanges.filter(da => ['Approved', 'Determined'].includes(da.changeTo)).length
  ), [statusChanges]);

  const assessmentChangeCount = useMemo(() => (
    statusChanges.filter(da => da.changeTo === 'Under Assessment').length
  ), [statusChanges]);

  const actionItems = useMemo(() => {
    const items = [];
    dueFollowUps.forEach(da => items.push({
      key: `follow-${da.id}`,
      type: 'Follow-up',
      color: COLORS.amber,
      title: sAddr(da.address) || da.da_number,
      detail: da.follow_up_reason || followUpLabel(da.next_follow_up_at),
      da,
    }));
    statusChanges.forEach(da => items.push({
      key: `status-${da.id}`,
      type: 'Status',
      color: STATUS_COLORS[da.changeTo]?.dot || COLORS.green,
      title: sAddr(da.address) || da.da_number,
      detail: `${da.changeFrom} → ${da.changeTo}`,
      da,
    }));
    upcomingFollowUps.slice(0, 3).forEach(da => items.push({
      key: `upcoming-${da.id}`,
      type: 'Next',
      color: COLORS.textMuted,
      title: sAddr(da.address) || da.da_number,
      detail: `${followUpLabel(da.next_follow_up_at)}${da.follow_up_reason ? ` · ${da.follow_up_reason}` : ''}`,
      da,
    }));
    return items.slice(0, 6);
  }, [dueFollowUps, statusChanges, upcomingFollowUps]);

  // Stats breakdowns
  const activeCount      = useMemo(() => tracked.filter(da => ['Lodged', 'Under Assessment'].includes(da.status)).length, [tracked]);
  const exhibitionCount  = useMemo(() => tracked.filter(da => da.status === 'On Exhibition').length, [tracked]);
  const approvedCount    = useMemo(() => tracked.filter(da => ['Approved', 'Determined'].includes(da.status)).length, [tracked]);

  const iSt = {
    width: "100%", padding: "10px 12px", borderRadius: "8px",
    border: `1px solid ${theme.border}`, background: theme.bgCard,
    color: theme.text, fontSize: "var(--text-input, 16px)", fontFamily: "inherit",
    boxSizing: "border-box", outline: "none", marginBottom: "8px",
  };

  const addMyDA = async () => {
    const ref = myIn.ref.trim();
    if (!ref || addLoading) return;
    setAddStatus("");
    const norm = ref.toLowerCase();
    const ex = apps.find(a =>
      [a.da_number, a.council_application_number, a.planning_portal_app_number]
        .some(v => (v || "").toLowerCase() === norm)
    );
    if (ex) {
      if (tracked.some(t => t.id === ex.id)) {
        setAddStatus("This DA is already tracked.");
        return;
      }
      setAddLoading(true);
      try {
        if (togTrack) {
          await togTrack(ex);
        } else {
          setTracked(p => p.find(t => t.id === ex.id) ? p : [...p, { ...ex, notes: "", added: new Date().toISOString().split("T")[0] }]);
        }
        setMyIn({ ref: "" });
        setShowAddDA(false);
      } finally {
        setAddLoading(false);
      }
    } else {
      setAddStatus("DA not found in the current council records yet. Use search first, then track it from the DA card.");
    }
  };

  const handleUntrack = async (da) => {
    if (togTrack) {
      await togTrack(da);
    } else {
      setTracked(p => p.filter(t => t.id !== da.id));
    }
    if (expId === da.id) setExpId(null);
  };

  return (
    <div style={{
      maxWidth: isMobile ? "100%" : "1180px",
      margin: "0 auto",
      padding: isMobile ? "16px" : "18px 24px 28px",
      boxSizing: "border-box",
    }}>

      {/* Activity / Pipeline toggle — Pro only, at top */}
      {isPro && (
        <div style={{ display: 'flex', gap: 6, marginBottom: 16 }}>
          {[{ key: 'activity', label: 'Activity' }, { key: 'pipeline', label: 'Pipeline' }].map(({ key, label }) => {
            const active = pipelineTab === key;
            return (
              <button
                key={key}
                onClick={() => setPipelineTab(key)}
                style={{
                  padding: '6px 16px', borderRadius: 20,
                  border: `1px solid ${active ? COLORS.amber : COLORS.cardBorder}`,
                  background: active ? COLORS.amber : COLORS.card,
                  color: active ? COLORS.bg : COLORS.text,
                  fontSize: 13, fontWeight: active ? 700 : 500,
                  cursor: 'pointer', fontFamily: "'DM Sans', sans-serif",
                  transition: 'all 0.15s',
                }}
              >{label}</button>
            );
          })}
        </div>
      )}

      {/* Pipeline summary view */}
      {isPro && pipelineTab === 'pipeline' && (
        <div style={{ margin: '0 -16px' }}>
          <PipelineSummaryView tracked={tracked} updateStage={updateStage} theme={theme} />
        </div>
      )}

      {(!isPro || pipelineTab === 'activity') && (<>

        {/* Follow-up command centre — first thing users see */}
        {tracked.length > 0 && (
          <div style={{
            background: COLORS.card,
            border: `1px solid ${COLORS.cardBorder}`,
            borderLeft: `3px solid ${COLORS.amber}`,
            borderRadius: 12,
            padding: 18,
            boxShadow: "0 2px 12px rgba(0,0,0,0.4), 0 1px 3px rgba(0,0,0,0.25)",
            marginBottom: 16,
          }}>
            <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 10 }}>
              <span style={{ fontSize: 36, fontWeight: 800, color: COLORS.text }}>{dueFollowUps.length + statusChanges.length}</span>
              <span style={{ fontSize: 15, color: COLORS.textMuted }}>
                thing{dueFollowUps.length + statusChanges.length === 1 ? '' : 's'} to check
              </span>
            </div>
            {actionItems.length === 0 ? (
              <div style={{ color: COLORS.textDim, fontSize: 13 }}>No recorded changes or scheduled follow-ups due. Check your Contacted and Quoted projects for next steps.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {actionItems.map(item => (
                  <button
                    key={item.key}
                    onClick={() => onOpenDA ? onOpenDA(item.da) : setExpId(item.da.id)}
                    style={{
                      width: '100%', display: 'flex', alignItems: 'flex-start', gap: 10,
                      border: `1px solid ${COLORS.cardBorder}`, borderRadius: 9,
                      background: '#081525', padding: '10px 11px', textAlign: 'left',
                      cursor: 'pointer', fontFamily: 'inherit',
                    }}
                  >
                    <span style={{ width: 8, height: 8, borderRadius: 4, background: item.color, flexShrink: 0, marginTop: 5 }} />
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span style={{ display: 'block', color: COLORS.text, fontSize: 13, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.title}</span>
                      <span style={{ display: 'block', color: COLORS.textDim, fontSize: 12, marginTop: 2 }}>{item.detail}</span>
                    </span>
                    <span style={{ color: item.color, fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.4, flexShrink: 0 }}>{item.type}</span>
                  </button>
                ))}
              </div>
            )}
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', fontSize: 12, marginTop: 10 }}>
              {approvedChangeCount > 0 && <span style={{ color: COLORS.green }}>{approvedChangeCount} newly approved</span>}
              {assessmentChangeCount > 0 && <span style={{ color: COLORS.textMuted }}>{assessmentChangeCount} moved to assessment</span>}
              {approaching.length > 0 && <span style={{ color: COLORS.orange }}>{approaching.length} exhibition closing this week</span>}
              {upcomingFollowUps.length > 0 && <span style={{ color: COLORS.amber }}>{upcomingFollowUps.length} follow-up{upcomingFollowUps.length !== 1 ? 's' : ''} this week</span>}
            </div>
            <div style={{ fontSize: 11, color: COLORS.textDim, marginTop: 8 }}>
              Updated {new Date().toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' })} · {tracked.length} tracked DA{tracked.length !== 1 ? 's' : ''}
            </div>
          </div>
        )}

        {/* Tracking Stats — collapsed by default */}
        {tracked.length > 0 && (
          <CollapsibleSection title="Tracking Stats" defaultOpen={false}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 8, marginBottom: 8 }}>
              {[
                { label: "Total Tracked",          value: tracked.length,   color: COLORS.text    },
                { label: "Active",                  value: activeCount,      color: COLORS.amber   },
                { label: "On Exhibition",           value: exhibitionCount,  color: COLORS.orange  },
                { label: "Approved / Determined",   value: approvedCount,    color: COLORS.green   },
              ].map(s => (
                <div key={s.label} style={{
                  background: COLORS.card, border: `1px solid ${COLORS.cardBorder}`,
                  borderRadius: 10, padding: "10px 12px",
                }}>
                  <div style={{ color: COLORS.textDim, fontSize: 10, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 2 }}>{s.label}</div>
                  <div style={{ color: s.color, fontSize: 22, fontWeight: 800 }}>{s.value}</div>
                </div>
              ))}
            </div>
          </CollapsibleSection>
        )}

        {/* Recent Activity */}
        {statusChanges.length > 0 && (
          <CollapsibleSection
            title="Recent Activity"
            defaultOpen={true}
            rightAction={<span style={{ color: COLORS.amber, fontSize: 11, fontWeight: 600, cursor: "pointer" }}>See all</span>}
          >
            <div style={{ marginBottom: 8 }}>
              {statusChanges.slice(0, 5).map(da => {
                const ago = timeAgoStr(da.updated_at);
                return (
                  <div key={da.id} style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "12px 0", borderBottom: `1px solid ${COLORS.cardBorder}` }}>
                    <span style={{
                      width: 8, height: 8, borderRadius: 4,
                      background: STATUS_COLORS[da.changeTo]?.dot || '#888',
                      flexShrink: 0, marginTop: 6,
                    }} />
                    <div style={{ flex: 1 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4, flexWrap: "wrap" }}>
                        {da.changeFrom && <MiniStatusBadge status={da.changeFrom} />}
                        {da.changeFrom && <span style={{ color: COLORS.textDim, fontSize: 13 }}>→</span>}
                        <MiniStatusBadge status={da.changeTo} />
                      </div>
                      <div style={{ color: COLORS.text, fontSize: 14, fontWeight: 600 }}>
                        {(sAddr(da.address) || da.da_number || '').split(',')[0]}
                      </div>
                    </div>
                    {ago && <div style={{ color: COLORS.textDim, fontSize: 12, whiteSpace: "nowrap", flexShrink: 0, marginTop: 2 }}>{ago}</div>}
                  </div>
                );
              })}
            </div>
          </CollapsibleSection>
        )}

        {/* Tracked DA list */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10, marginTop: 4 }}>
          <span style={{ fontSize: 11, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: 1, fontWeight: 600 }}>
            Your Tracked DAs ({tracked.length})
          </span>
          <button
            onClick={() => { setShowAddDA(p => !p); setAddStatus(""); }}
            style={{ background: "none", border: `1px solid ${COLORS.cardBorder}`, borderRadius: 6, color: COLORS.textMuted, fontSize: 11, fontWeight: 600, cursor: "pointer", padding: "4px 10px", fontFamily: "inherit" }}
          >+ Add</button>
        </div>

        {/* Add DA inline */}
        {showAddDA && (
          <div style={{ marginBottom: 12, padding: "14px 16px", background: COLORS.card, border: `1px solid ${COLORS.cardBorder}`, borderRadius: 10 }}>
            <input type="text" placeholder="DA reference (e.g. DA/2026/0041)" value={myIn.ref} onChange={e => { setMyIn({ ref: e.target.value }); setAddStatus(""); }}
              onKeyDown={e => { if (e.key === 'Enter') addMyDA(); if (e.key === 'Escape') setShowAddDA(false); }}
              style={iSt} autoFocus />
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={addMyDA} disabled={addLoading} style={{ flex: 1, padding: "9px", borderRadius: "6px", border: "none", background: COLORS.amber, color: COLORS.bg, fontSize: "13px", fontWeight: "600", cursor: addLoading ? "default" : "pointer", fontFamily: "inherit", opacity: addLoading ? 0.65 : 1 }}>{addLoading ? "Adding..." : "Add"}</button>
              <button onClick={() => setShowAddDA(false)} style={{ padding: "9px 14px", borderRadius: "6px", border: `1px solid ${COLORS.cardBorder}`, background: "transparent", color: COLORS.textMuted, fontSize: "13px", cursor: "pointer", fontFamily: "inherit" }}>Cancel</button>
            </div>
            <div style={{ fontSize: "10px", color: addStatus ? COLORS.orange : COLORS.textDim, marginTop: "8px" }}>{addStatus || 'Or tap "+ Track this DA" on any application card.'}</div>
          </div>
        )}

        {tracked.length > 0 && (
          <input
            type="text"
            placeholder="Search DA number or address…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ ...iSt, marginBottom: "12px" }}
          />
        )}

        {(() => {
          const q = search.trim().toLowerCase();
          const visible = q === "" ? tracked : tracked.filter(da =>
            [
              da.da_number,
              da.council_application_number,
              da.planning_portal_app_number,
              da.address,
              sAddr(da.address),
              da.description,
              da.status,
              da.note_text,
              da.notes,
              da.follow_up_reason,
              da.contact_name,
              da.contact_method,
            ].some(f => (f || "").toLowerCase().includes(q))
          );
          if (tracked.length === 0) return (
            <div style={{ textAlign: "center", padding: "40px", color: theme.textMuted, fontSize: "14px" }}>
              No DAs tracked yet.<br />Tap "+ Add" above or tap "+ Track this DA" on any application.
            </div>
          );
          if (visible.length === 0) return (
            <div style={{ textAlign: "center", padding: "40px", color: theme.textMuted, fontSize: "14px" }}>No tracked DAs match your search.</div>
          );
          return (
            <div style={{ display: "flex", flexDirection: "column" }}>
              {visible.map(da => {
                const changed = statusChanges.find(c => c.id === da.id);
                return (
                  <div key={da.id}>
                    <DACard
                      app={da}
                      tracked={true}
                      theme={theme}
                      onShowOnMap={onShowOnMap}
                      onOpenDA={onOpenDA}
                      updateStage={isPro ? updateStage : undefined}
                    />
                    {/* Change annotation + untrack */}
                    <div style={{
                      display: "flex", justifyContent: "space-between", alignItems: "center",
                      marginTop: -8, marginBottom: 14, paddingLeft: 14,
                    }}>
                      {changed ? (
                        <span style={{ color: COLORS.green, fontSize: 12, fontWeight: 600 }}>
                          • Changed: {changed.changeFrom} → {changed.changeTo}
                        </span>
                      ) : (
                        <span style={{ color: COLORS.textDim, fontSize: 12, fontWeight: 400 }}>No changes since last visit</span>
                      )}
                      <button
                        onClick={() => handleUntrack(da)}
                        style={{ background: "none", border: "none", color: COLORS.textDim, fontSize: 12, fontWeight: 500, cursor: "pointer", fontFamily: "inherit", padding: "4px 8px" }}
                      >Untrack ✕</button>
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })()}

        <div style={{ borderTop: `1px solid ${theme.border}`, marginTop: "24px", padding: "12px 0", textAlign: "center", fontSize: "10px", color: theme.textMuted }}>
          DA data © State of New South Wales (Department of Planning, Housing and Infrastructure). Licensed under{' '}
          <a href="https://creativecommons.org/licenses/by/4.0/" target="_blank" rel="noopener noreferrer" style={{ color: theme.textMuted, textDecoration: "underline" }}>Creative Commons Attribution</a>
          . Data has been transformed for display.
        </div>
      </>)}
    </div>
  );
}
